import { Link } from '@tanstack/react-router';
import { ShieldCheck, UsersRound } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { DataTable, useDataTable } from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Member, StaffProfileStoresItem } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { session } from '@/shared/session/session';

import { EditMemberDialog } from '../components/edit-member-dialog';
import { EffectivePermissionsSheet } from '../components/effective-permissions-sheet';
import { InviteMemberDialog } from '../components/invite-member-dialog';
import { useMemberColumns } from '../components/member-columns';
import { memberFilters } from '../components/member-filters';
import { useMemberRowActions, type MemberRowAction } from '../components/member-row-actions';
import { RemoveMemberDialog } from '../components/remove-member-dialog';
import { useResendInvitation } from '../hooks/use-member-mutations';
import { useMembers } from '../hooks/use-members';
import { resendErrorKey } from '../schemas/member-rules';

const EMPTY: readonly Member[] = [];
const NO_STORES: readonly StaffProfileStoresItem[] = [];

function Team() {
  const { t } = useTranslation('team');
  const { me } = session.useSession();
  const { can } = usePermissions();
  const query = useMembers();
  const resend = useResendInvitation();
  const message = useErrorMessage();
  const [editing, setEditing] = useState<Member | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [viewing, setViewing] = useState<Member | null>(null);

  const canManage = can('staff.manage');
  const actor = useMemo(
    () => (me && canManage ? { userId: me.user.id, roleKey: me.role.key } : null),
    [canManage, me],
  );
  const members = query.data ?? EMPTY;

  const { mutate: resendInvitation } = resend;
  const onAction = useCallback(
    (action: MemberRowAction, member: Member) => {
      if (action === 'edit') {
        setEditing(member);
      } else if (action === 'permissions') {
        setViewing(member);
      } else if (action === 'remove') {
        setRemoving(member);
      } else {
        resendInvitation(member.id, {
          onError: (error) => {
            const key = resendErrorKey(error);
            toast.error(key ? t(key) : message(error));
          },
        });
      }
    },
    [message, resendInvitation, t],
  );

  const columns = useMemberColumns({
    selfId: me?.user.id,
    stores: me?.stores ?? NO_STORES,
    onShowAccess: setViewing,
  });
  const rowActions = useMemberRowActions({ actor, members, onAction });
  const filters = useMemo(() => memberFilters(t, members), [members, t]);
  const table = useDataTable({
    data: members,
    columns,
    filters,
    getRowId: (row) => row.id,
    initialColumnVisibility: { email: false },
  });

  const invite = (tour?: boolean) => (
    <Can permission="staff.manage">
      <InviteMemberDialog tour={tour} />
    </Can>
  );

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description')}
        meta={
          query.data ? (
            <Badge variant="secondary">{t('count', { count: members.length })}</Badge>
          ) : null
        }
        actions={
          <>
            <Button asChild variant="outline" data-tour="org.team.roles-link">
              <Link to="/org/roles">
                <ShieldCheck aria-hidden />
                {t('roles.title')}
              </Link>
            </Button>
            {invite(true)}
          </>
        }
      />
      {query.data && members.length === 0 ? (
        <EmptyState
          icon={UsersRound}
          title={t('empty.title')}
          description={t('empty.description')}
          action={invite()}
        />
      ) : (
        <DataTable
          table={table}
          search={{ label: t('search'), placeholder: t('search') }}
          rowActions={rowActions}
          tour={{ table: 'org.team.table', rowActions: 'org.team.row-actions' }}
          getRowLabel={(row) => row.user.name ?? row.user.email}
          resourceName={{ singular: t('resource_one'), plural: t('resource_other') }}
          loading={query.isPending}
          fetching={query.isFetching || resend.isPending}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      )}
      <EditMemberDialog
        member={editing}
        members={members}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
      <EffectivePermissionsSheet
        member={viewing}
        onOpenChange={(open) => {
          if (!open) setViewing(null);
        }}
      />
      <RemoveMemberDialog
        member={removing}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
      />
    </PageContent>
  );
}

export function TeamPage() {
  return (
    <FeatureErrorBoundary name="team">
      <Team />
    </FeatureErrorBoundary>
  );
}
